import { eq } from "drizzle-orm";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { db } from "@/db/db";
import { feed } from "@/db/schema";
import type { DirectWsInvitation, Feed } from "@/db/types";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { currentFeedPendingInvitesSelect, currentFeedQuery, myFeedPermissionsQuery } from "@/models/repositories";
import accountInfo, { type AccountInfo } from "../accountInfo";
import { WorkspaceContext } from "./workspaceProvider";

type FeedState = {
  currentFeedId?: string;
  currentFeed?: Feed;
  currentFeedAccounts?: Map<string, AccountInfo>;
  currentFeedPendingInvites?: DirectWsInvitation[];
  isCurrentFeedAdmin?: boolean;
  haveWritePermission?: boolean;
};

export const CurrentFeedContext = createContext<FeedState>({});

const CurrentFeedProvider = ({ children }) => {
  const { feedId: currentFeedId } = useParams();
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);

  const feedAccounts = [];

  const currentFeeds =
    useReactiveSelectQuery(currentFeedQuery, [{ currentFeedId }], "CurrentFeedProvider:currentFeedQuery") ?? [];
  const currentFeed = currentFeeds?.[0];

  const currentFeedAccounts = useMemo(() => accountInfo(feedAccounts), [feedAccounts]);

  const myFeedPermissions =
    useReactiveSelectQuery(
      myFeedPermissionsQuery,
      [{ currentFeedId, myCurrentWorkspaceMembershipId: myCurrentWorkspaceMembership?.id }],
      "CurrentFeedProvider:myFeedPermissionsQuery",
    ) ?? [];

  const isCurrentFeedAdmin = useMemo(() => myFeedPermissions?.some((p) => p.name === "admin"), [myFeedPermissions]);
  const haveWritePermission = useMemo(() => myFeedPermissions?.some((p) => p.name === "write"), [myFeedPermissions]);

  const currentFeedPendingInvites =
    useReactiveSelectQuery(
      currentFeedPendingInvitesSelect,
      [{ currentFeedId }],
      "CurrentFeedProvider:currentFeedPendingInvitesSelect",
    ) ?? [];

  useEffect(() => {
    if (currentFeedId) {
      db.update(feed)
        .set({ lastOpened: new Date().toISOString() })
        .where(eq(feed.id, currentFeedId))
        .execute()
        .catch((e) => console.error("[CurrentFeedProvider] Failed to update lastOpened", e));
    }
  }, [currentFeedId]);

  return (
    <CurrentFeedContext.Provider
      value={{
        currentFeedId,
        currentFeed,
        currentFeedAccounts,
        currentFeedPendingInvites,
        isCurrentFeedAdmin,
        haveWritePermission,
      }}
    >
      {children}
    </CurrentFeedContext.Provider>
  );
};

export default CurrentFeedProvider;
